import { Clock, EnvelopeSimple } from '@phosphor-icons/react';
import { useLocalStorageState } from 'ahooks';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import { FirebaseError } from 'firebase/app';
import {
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCustomToken,
  signInWithPopup,
} from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate } from 'react-router-dom';
import { clearCachedToken, get, post, renewToken } from 'src/api/requests';
import { InfoPanel } from 'src/components/InfoPanel';
import { Input } from 'src/components/Input';
import { PasswordInput } from 'src/components/PasswordInput';
import { UnloggedCard } from 'src/components/UnloggedCard';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import { useServiceMode } from 'src/contexts/ServiceModeContext';
import { logoutHelper } from 'src/helpers/logoutHelper';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { cn } from 'src/utils/className';
import { getFirebaseErrorMessage } from 'src/utils/FirebaseErrorUtils';
import { getPayloadJWT } from 'src/utils/jwt';
import { AccountDisclaimers } from '../components/AccountDisclaimers';
import { Button } from '../components/Button';
import { Loading } from '../components/Loading';
import { Tooltip } from '../components/Tooltip';

export const Login = () => {
  const [loading, setLoading] = useState(false);
  const [, setFireBaseDisplayName] = useLocalStorageState<string | null>(
    '@control-plane/fireBaseDisplayName',
    {
      defaultValue: '',
      listenStorageChange: true,
    },
  );
  const [, setIsClosedAlphaVisible] = useLocalStorageState<boolean>(
    '@control-plane/closed-alpha-visible',
    {
      defaultValue: false,
      listenStorageChange: true,
    },
  );
  const [lastEmailVerificationAttempt, setLastEmailVerificationAttempt] =
    useLocalStorageState<string>(
      '@control-plane/last-email-verification-attempt',
      {
        defaultValue: '',
        listenStorageChange: true,
      },
    );
  const [, setLastAuthProvider] = useLocalStorageState<
    'email' | 'google' | 'apple' | 'facebook' | null
  >('@control-plane/last-auth-provider', {
    defaultValue: null,
    listenStorageChange: true,
  });

  const notification = useNotification();
  const { environment, updateEnvironment } = useEnvironment();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const auth = getAuth();
  const { revokeServiceKey } = useServiceMode();
  const googleProvider = new GoogleAuthProvider();
  const facebookProvider = new FacebookAuthProvider();
  const appleProvider = new OAuthProvider('apple.com');

  const { identify } = useAnalytics();
  const [showVerifyEmailPanel, setShowVerifyEmailPanel] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const resolveRecaptchaToken = async (action: string) => {
    if (!executeRecaptcha) {
      throw new Error('recaptcha_unavailable');
    }

    const token = await executeRecaptcha(action);

    if (!token) {
      throw new Error('recaptcha_failed');
    }

    return token;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setShowVerifyEmailPanel(false);

    setLoading(true);

    try {
      const captchaToken = await resolveRecaptchaToken('login');

      const { loginToken } = await post(
        '/auth/login',
        {
          email,
          password,
        },
        {
          headers: {
            'x-recaptcha-token': captchaToken,
            'x-recaptcha-action': 'login',
          },
          withoutToken: true,
        },
      );

      const userCredential = await signInWithCustomToken(auth, loginToken);
      const user = userCredential.user;

      if (!user?.emailVerified) {
        const now = Date.now();
        const timeSinceLastAttempt = lastEmailVerificationAttempt
          ? now - parseInt(lastEmailVerificationAttempt)
          : Infinity;

        if (timeSinceLastAttempt > 60000) {
          try {
            await post('/users/me/verification-email', {});
            setLastEmailVerificationAttempt(now.toString());
          } catch {
            // Ignore resend errors; show inline panel instead
          }
        }

        setShowVerifyEmailPanel(true);

        logoutHelper(false);
        setLoading(false);
        return;
      }

      setFireBaseDisplayName(user.displayName);

      if (user) {
        const [fname, lname] = user?.displayName?.split(' ') || [];

        try {
          await post(`/users`, {
            fname,
            lname,
            email,
          });
          await renewToken();
        } catch (error) {
          if (error instanceof Error) {
            if (error?.message !== 'User found in the system') {
              notification.error(error.message);
              return;
            }
          }
          const err = error as AxiosError;
          if (err.status === 403) {
            notification.error(JSON.parse(err.request.response).message);
            return;
          }
        }

        const environments = await get('/environments');

        const isStoredEnvInEnvironments = environments.find(
          (env: { id: string }) => env.id === environment?.envId,
        );

        const initialEnvId =
          environment?.envId &&
          environment.userId === user.uid &&
          isStoredEnvInEnvironments
            ? environment.envId
            : environments[0].id;

        updateEnvironment({
          userId: user.uid,
          envId: initialEnvId,
        });

        await post(`/users/sync`, {}, { envId: initialEnvId });
        await revokeServiceKey();

        const token = await user.getIdToken();
        const decoded = getPayloadJWT(token);

        identify(auth.currentUser?.uid || '', {
          email: user.email,
          name: user.displayName,
          loginMethod: 'email',
          loginDate: new Date().toISOString(),
          organizationId: decoded.organization?.id || '',
          organizationName: decoded.organization?.name || '',
          environmentId: decoded.organization?.environments?.[0]?.id || '',
        });

        setLastAuthProvider('email');
        clearCachedToken();

        setLoading(true);

        // sleep 2 seconds to make sure the environment is updated
        // await new Promise((resolve) => setTimeout(resolve, 2000));
        // TODO: remove the previous line comment, after some tests were done
        // in case of the issues with the environment update was solved

        const organizations = await get(`/users/${decoded.id}/organizations`);

        if (organizations && organizations.length > 1) {
          navigate('/settings/organizations');
        } else {
          navigate('/');
        }
      }
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        notification.error(getFirebaseErrorMessage(error.code));
      } else {
        console.error('Login error:', error);
        notification.error(
          'Something went wrong. Please try again in a moment.',
        );
      }
    }

    setLoading(false);
  };

  const handleSocialSignIn = async (
    provider: GoogleAuthProvider | FacebookAuthProvider | OAuthProvider,
    providerName: string,
  ) => {
    try {
      setLoading(true);

      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      setFireBaseDisplayName(user.displayName);

      if (user) {
        const [fname, lname] = user?.displayName?.split(' ') || [];
        const userEmail = user.email || '';

        try {
          await post(`/users`, {
            fname,
            lname,
            email: userEmail,
          });
          await renewToken();
        } catch (error) {
          if (error instanceof Error) {
            if (error?.message !== 'User found in the system') {
              notification.error(error.message);
              return;
            }
          }
          const err = error as AxiosError;
          if (err.status === 403) {
            notification.error(JSON.parse(err.request.response).message);
            return;
          }
        }

        const environments = await get('/environments');

        const isStoredEnvInEnvironments = environments.find(
          (env: { id: string }) => env.id === environment?.envId,
        );

        const initialEnvId =
          environment?.envId &&
          environment.userId === user.uid &&
          isStoredEnvInEnvironments
            ? environment.envId
            : environments[0].id;

        updateEnvironment({
          userId: user.uid,
          envId: initialEnvId,
        });

        await post(`/users/sync`, {}, { envId: initialEnvId });
        const token = await user.getIdToken();
        const decoded = getPayloadJWT(token);

        identify(auth.currentUser?.uid || '', {
          email: user.email,
          name: user.displayName,
          loginMethod: providerName.toLowerCase(),
          loginDate: new Date().toISOString(),
        });

        setLastAuthProvider(
          providerName.toLowerCase() as 'google' | 'apple' | 'facebook',
        );
        clearCachedToken();

        notification.success(`Login with ${providerName} successful!`);

        // sleep 2 seconds to make sure the environment is updated
        // await new Promise((resolve) => setTimeout(resolve, 2000));
        // TODO: remove the previous line comment, after some tests were done
        // in case of the issues with the environment update was solved

        const organizations = await get(`/users/${decoded.id}/organizations`);

        if (organizations && organizations.length > 1) {
          navigate('/settings/organizations');
        } else {
          navigate('/');
        }
      }
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        notification.error(getFirebaseErrorMessage(error.code));
      } else {
        notification.error(`Error during ${providerName} login`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => handleSocialSignIn(googleProvider, 'Google');
  const handleFacebookSignIn = () =>
    handleSocialSignIn(facebookProvider, 'Facebook');
  const handleAppleSignIn = () => handleSocialSignIn(appleProvider, 'Apple');

  useEffect(() => {
    setIsClosedAlphaVisible(false);
  }, [setIsClosedAlphaVisible]);

  return (
    <UnloggedCard>
      <img
        src="/assets/logo-white.png"
        alt="Circle"
        className={clsx('absolute top-10 left-10 h-10 animate-fade-in-up z-10')}
      />

      <div
        className={clsx(
          'absolute top-10 right-10 h-10 animate-fade-in-up z-10',
        )}
      >
        <a
          href="https://fluents.ai/contact-us"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium opacity-100 text-neutral-0"
        >
          Contact Us
        </a>
      </div>

      <h2 className="mb-6">Sign in to your account</h2>

      <form onSubmit={handleSubmit}>
        <div className="mb-4 relative">
          <LastUsedChip provider="email" />

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            required
          />
        </div>

        <div className="mb-6">
          <PasswordInput
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            required
          />
        </div>

        <Button className="w-full mb-6" type="submit" disabled={loading}>
          {loading ? (
            <div className="flex gap-2 items-center justify-center">
              <Loading size={'4'} />
              <p>Loading...</p>
            </div>
          ) : (
            'Sign In'
          )}
        </Button>

        {showVerifyEmailPanel && (
          <InfoPanel
            dataTestId="verification-message"
            className="mb-6"
            variant="primary"
            icon={<EnvelopeSimple className="h-5 w-5" />}
            title="Verify your email to sign in."
            description="We sent a message with a verification link—please check your inbox and follow the link."
          />
        )}

        <div className="relative mb-6">
          <div className="flex items-center justify-center">
            <div className="w-full border-t border-neutral-200"></div>
            <span className="absolute bg-neutral-0 px-2 text-neutral-500">
              or
            </span>
          </div>
        </div>

        <div className="flex flex-col">
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className={clsx(
              'w-full mb-4 h-10 relative flex items-center justify-center rounded border border-neutral-200 bg-neutral-0 hover:bg-neutral-50',
              loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            )}
          >
            <LastUsedChip provider="google" />

            <div className="absolute left-4">
              <img src="/assets/google.svg" width={22} height={22} />
            </div>
            {loading ? (
              <div className="flex gap-2 items-center justify-center">
                <Loading size={'4'} />
                <p>Loading...</p>
              </div>
            ) : (
              <span className="text-sm font-medium text-neutral-600">
                Sign in with Google
              </span>
            )}
          </button>

          {process.env.REACT_APP_ENABLE_SOCIAL_FACEBOOK === 'true' && (
            <button
              onClick={handleFacebookSignIn}
              disabled={loading}
              className={clsx(
                'w-full mb-4 h-10 relative flex items-center justify-center rounded border border-neutral-200 bg-neutral-0 hover:bg-neutral-50',
                loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <LastUsedChip provider="facebook" />

              <div className="absolute left-4">
                <img src="/assets/facebook.svg" width={22} height={22} />
              </div>
              {loading ? (
                <div className="flex gap-2 items-center justify-center">
                  <Loading size={'4'} />
                  <p>Loading...</p>
                </div>
              ) : (
                <span className="text-sm font-medium text-neutral-600">
                  Sign in with Facebook
                </span>
              )}
            </button>
          )}

          {process.env.REACT_APP_ENABLE_SOCIAL_APPLE === 'true' && (
            <button
              onClick={handleAppleSignIn}
              disabled={loading}
              className={clsx(
                'w-full mb-6 h-10 relative flex items-center justify-center rounded border border-neutral-200 bg-neutral-0 hover:bg-neutral-50',
                loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <LastUsedChip provider="apple" />

              <div className="absolute left-4">
                <img src="/assets/apple.svg" width={22} height={22} />
              </div>
              {loading ? (
                <div className="flex gap-2 items-center justify-center">
                  <Loading size={'4'} />
                  <p>Loading...</p>
                </div>
              ) : (
                <span className="text-sm font-medium text-neutral-600">
                  Sign in with Apple
                </span>
              )}
            </button>
          )}

          <div className="text-center text-body mb-4">
            Don't have an account?{' '}
            <span
              className={clsx(
                'w-fulltext-center font-medium text-body cursor-pointer',
                loading
                  ? 'opacity-50 text-neutral-300'
                  : 'opacity-100 text-primary',
              )}
              onClick={() => navigate('/signup')}
            >
              Sign Up
            </span>
          </div>

          <a
            className={clsx(
              'w-full mb-4 text-center font-medium text-body cursor-pointer',
              loading
                ? 'opacity-50 text-neutral-300'
                : 'opacity-100 text-primary',
            )}
            onClick={() => navigate('/reset-password')}
          >
            Forgot Password
          </a>

          <AccountDisclaimers className="text-neutral-700" alignment="center" />
        </div>
      </form>
    </UnloggedCard>
  );
};

const LastUsedChip = ({
  provider,
}: {
  provider: 'email' | 'google' | 'apple' | 'facebook';
}) => {
  const [lastAuthProvider] = useLocalStorageState<
    'email' | 'google' | 'apple' | 'facebook' | null
  >('@control-plane/last-auth-provider', {
    defaultValue: null,
    listenStorageChange: true,
  });

  if (lastAuthProvider !== provider) {
    return <></>;
  }

  return (
    <Tooltip
      content="You last signed in with this method"
      position="top"
      theme="light"
      className={cn(
        'absolute top-0 right-2',
        provider === 'email' ? 'top-0' : 'top-2',
      )}
    >
      <span
        className={cn(
          'inline-flex items-center justify-center text-neutral-500 bg-neutral-100 rounded-full px-0.5 py-0.5',
          provider === 'email' ? 'top-0' : 'top-2',
        )}
      >
        <Clock className="h-4 w-4" />
      </span>
    </Tooltip>
  );
};
